import {
  TASK_PRIORITIES,
  type TaskDetail,
  type TaskFormOptions,
  type TaskPriority,
  type TaskSummary,
} from '@fos/shared/contracts/work';
import { TASK_PRIORITY_LABELS } from '@fos/shared/labels';
import { Plus } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button, type ButtonProps } from '../../../ui/Button';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { Dot } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { useTaskOptions } from '../hooks/useTasks';

/**
 * Dialog „Nowe zadanie” (i „Edytuj zadanie”): tytuł, klient → projekt / usługa, wykonawca, termin, priorytet, opis.
 * Zmiana klienta czyści projekt i usługę innego klienta (serwer i tak pilnuje spójności), wybór projektu
 * ustawia jego klienta. Otwierany z `/zadania?nowe=1&klient=ID`, przycisku „Nowe zadanie” i profili klienta/projektu.
 */

const NONE = '__brak__';

type FieldName =
  | 'title'
  | 'clientId'
  | 'projectId'
  | 'serviceId'
  | 'assigneeUserId'
  | 'dueDate'
  | 'priority'
  | 'descriptionMd';

interface FormValues {
  title: string;
  clientId: string | null;
  projectId: string | null;
  serviceId: string | null;
  assigneeUserId: string | null;
  dueDate: string;
  priority: TaskPriority;
  descriptionMd: string;
}

export interface NewTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  defaultServiceId?: string | null;
  /** tytuł i opis na start (np. temat i zajawka maila w „Utwórz zadanie” z wiadomości) */
  defaultTitle?: string;
  defaultDescription?: string;
  /**
   * Zadanie z wiadomości (Inbox / wątek poczty, Figma 02 „Utwórz zadanie”): zapis przez `mail.createTask` —
   * pochodzenie „Z wiadomości” i link do wątku w karcie zadania. `label` = „wiadomość od Anny Kowalczyk”.
   */
  sourceMessage?: { id: string; label?: string } | null;
  /** Po zapisie (domyślnie: toast z linkiem do zadania). */
  onCreated?: (task: TaskSummary) => void;
}

/** Nowe zadanie. Props: `defaultClientId?`, `defaultProjectId?` (profil klienta / projektu). */
export function NewTaskDialog(props: NewTaskDialogProps) {
  return <TaskFormDialog mode="create" {...props} />;
}

/** Przycisk „Nowe zadanie” z własnym dialogiem (do wstawienia w profil klienta / projektu). */
export function NewTaskButton({
  defaultClientId,
  defaultProjectId,
  defaultServiceId,
  label = 'Nowe zadanie',
  variant = 'secondary',
  size,
}: {
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  defaultServiceId?: string | null;
  label?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
}) {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  if (!access.can('tasks', 'edit')) return null;
  return (
    <>
      <Button variant={variant} size={size} icon={Plus} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <NewTaskDialog
        open={open}
        onOpenChange={setOpen}
        defaultClientId={defaultClientId}
        defaultProjectId={defaultProjectId}
        defaultServiceId={defaultServiceId}
      />
    </>
  );
}

/** Edycja wszystkich pól zadania (menu „⋯” w karcie). */
export function EditTaskDialog({
  open,
  onOpenChange,
  task,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskDetail;
}) {
  return <TaskFormDialog mode="edit" open={open} onOpenChange={onOpenChange} task={task} />;
}

function initialValues(
  mode: 'create' | 'edit',
  task: TaskDetail | undefined,
  defaults: {
    clientId?: string | null;
    projectId?: string | null;
    serviceId?: string | null;
    title?: string;
    description?: string;
  },
  options: TaskFormOptions | undefined,
  me: string,
): FormValues {
  if (mode === 'edit' && task) {
    return {
      title: task.title,
      clientId: task.client?.id ?? null,
      projectId: task.project?.id ?? null,
      serviceId: task.service?.id ?? null,
      assigneeUserId: task.assignee?.userId ?? null,
      dueDate: task.dueDate ?? '',
      priority: task.priority,
      descriptionMd: task.descriptionMd ?? '',
    };
  }
  const project = defaults.projectId ? options?.projects.find((p) => p.id === defaults.projectId) : undefined;
  const service = defaults.serviceId ? options?.services.find((s) => s.id === defaults.serviceId) : undefined;
  const meCanTasks = options?.members.some((m) => m.userId === me && m.canTasks) ?? true;
  return {
    title: defaults.title ?? '',
    clientId: project?.clientId ?? service?.clientId ?? defaults.clientId ?? null,
    projectId: defaults.projectId ?? null,
    serviceId: defaults.serviceId ?? null,
    assigneeUserId: meCanTasks ? me : null,
    dueDate: '',
    priority: 'normal',
    descriptionMd: defaults.description ?? '',
  };
}

function TaskFormDialog({
  mode,
  open,
  onOpenChange,
  task,
  defaultClientId,
  defaultProjectId,
  defaultServiceId,
  defaultTitle,
  defaultDescription,
  sourceMessage,
  onCreated,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: TaskDetail;
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  defaultServiceId?: string | null;
  defaultTitle?: string;
  defaultDescription?: string;
  sourceMessage?: { id: string; label?: string } | null;
  onCreated?: (task: TaskSummary) => void;
}) {
  const session = useActiveSession();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const options = useTaskOptions(open);
  const create = trpc.tasks.create.useMutation();
  const createFromMessage = trpc.mail.createTask.useMutation();
  const defaults = {
    clientId: defaultClientId,
    projectId: defaultProjectId,
    serviceId: defaultServiceId,
    title: defaultTitle,
    description: defaultDescription,
  };
  const update = trpc.tasks.update.useMutation();
  const [values, setValues] = useState<FormValues>(() =>
    initialValues(mode, task, defaults, undefined, session.user.id),
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  const dirty = useRef(false);
  const wasOpen = useRef(false);
  // Świeże wartości przy każdym otwarciu (i po wczytaniu słowników: klient z domyślnego projektu).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu i po wczytaniu opcji
  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    // Po wczytaniu słowników nie nadpisujemy tego, co osoba już wpisała.
    if (wasOpen.current && dirty.current) return;
    if (!wasOpen.current) dirty.current = false;
    wasOpen.current = true;
    setValues(initialValues(mode, task, defaults, options.data, session.user.id));
    setErrors({});
    setFormError(null);
  }, [open, options.data === undefined]);

  // zgłoszenie z portalu klienta (17.10.2026): klient stały — backend odrzuca zmianę (TASK_MESSAGES.portalRequestClientLocked)
  const clientLocked = mode === 'edit' && task?.origin === 'client_portal';
  const data = options.data;
  const clientById = useMemo(() => new Map(data?.clients.map((c) => [c.id, c]) ?? []), [data]);

  const clientOptions: ComboboxOption[] = useMemo(
    () => [
      { value: NONE, label: 'Bez klienta', description: 'zadanie wewnętrzne' },
      ...(data?.clients ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        leading: <ClientDot color={c.color} />,
      })),
    ],
    [data],
  );
  const projectOptions: ComboboxOption[] = useMemo(() => {
    const list = (data?.projects ?? []).filter((p) => (values.clientId ? p.clientId === values.clientId : true));
    return [
      { value: NONE, label: 'Bez projektu' },
      ...list.map((p) => ({
        value: p.id,
        label: p.name,
        description: values.clientId ? undefined : p.clientId ? clientById.get(p.clientId)?.name : 'wewnętrzny',
      })),
    ];
  }, [data, values.clientId, clientById]);
  const serviceOptions = useMemo(
    () => [
      { value: NONE, label: 'Bez usługi' },
      ...(data?.services ?? [])
        .filter((s) => s.clientId === values.clientId)
        .map((s) => ({ value: s.id, label: s.name })),
    ],
    [data, values.clientId],
  );
  const memberOptions: ComboboxOption[] = useMemo(
    () => [
      { value: NONE, label: 'Bez wykonawcy' },
      ...(data?.members ?? [])
        .filter((m) => m.canTasks || m.userId === values.assigneeUserId)
        .map((m) => ({ value: m.userId, label: m.userId === session.user.id ? `${m.name} (Ty)` : m.name })),
    ],
    [data, values.assigneeUserId, session.user.id],
  );

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    dirty.current = true;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const setClient = (raw: string) => {
    dirty.current = true;
    const clientId = raw === NONE ? null : raw;
    setValues((v) => {
      const project = data?.projects.find((p) => p.id === v.projectId);
      const service = data?.services.find((s) => s.id === v.serviceId);
      return {
        ...v,
        clientId,
        projectId: project && project.clientId === clientId ? v.projectId : null,
        serviceId: service && service.clientId === clientId ? v.serviceId : null,
      };
    });
    setErrors((e) => ({ ...e, clientId: undefined, projectId: undefined, serviceId: undefined }));
  };

  const setProject = (raw: string) => {
    dirty.current = true;
    const projectId = raw === NONE ? null : raw;
    const project = data?.projects.find((p) => p.id === projectId);
    setValues((v) => {
      const clientId = project ? project.clientId : v.clientId;
      const service = data?.services.find((s) => s.id === v.serviceId);
      return {
        ...v,
        projectId,
        clientId,
        serviceId: service && service.clientId === clientId ? v.serviceId : null,
      };
    });
    setErrors((e) => ({ ...e, clientId: undefined, projectId: undefined }));
  };

  const close = () => onOpenChange(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!values.title.trim()) {
      setErrors({ title: 'Tytuł jest wymagany' });
      return;
    }
    const fields = {
      title: values.title.trim(),
      clientId: values.clientId,
      projectId: values.projectId,
      serviceId: values.serviceId,
      assigneeUserId: values.assigneeUserId,
      dueDate: values.dueDate || null,
      priority: values.priority,
      descriptionMd: values.descriptionMd.trim() ? values.descriptionMd : null,
    };
    try {
      if (mode === 'create') {
        const saved = sourceMessage
          ? await createFromMessage.mutateAsync({ messageId: sourceMessage.id, ...fields })
          : await create.mutateAsync(fields);
        await utils.tasks.invalidate();
        close();
        if (onCreated) onCreated(saved);
        else {
          toast.success(`Dodano zadanie #${saved.number}`, {
            description: saved.title,
            action: { label: 'Otwórz', onClick: () => navigate(`/zadania/${saved.id}`) },
          });
        }
      } else if (task) {
        const patch: Record<string, unknown> = {};
        const before = initialValues('edit', task, {}, data, session.user.id);
        for (const key of Object.keys(fields) as (keyof typeof fields)[]) {
          const prev =
            key === 'dueDate'
              ? before.dueDate || null
              : key === 'descriptionMd'
                ? before.descriptionMd.trim()
                  ? before.descriptionMd
                  : null
                : key === 'title'
                  ? before.title
                  : before[key];
          if (fields[key] !== prev) patch[key] = fields[key];
        }
        if (Object.keys(patch).length > 0) {
          await update.mutateAsync({ id: task.id, version: task.version, ...patch });
          await utils.tasks.invalidate();
          toast.success('Zapisano zmiany');
        }
        close();
      }
    } catch (err) {
      const issues = validationIssues(err);
      const fieldErrors: Partial<Record<FieldName, string>> = {};
      for (const issue of issues) {
        const key = issue.path.split('.')[0] as FieldName;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length === 0) setFormError(errorMessage(err, 'Nie udało się zapisać zadania.'));
    }
  };

  const pending = create.isPending || createFromMessage.isPending || update.isPending;
  const loadingOptions = options.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={mode === 'create' ? 'Nowe zadanie' : 'Edytuj zadanie'}
      description={
        mode === 'create' && sourceMessage
          ? `Z wiadomości${sourceMessage.label ? `: ${sourceMessage.label}` : ''}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="task-form" loading={pending}>
            {mode === 'create' ? 'Dodaj zadanie' : 'Zapisz'}
          </Button>
        </>
      }
    >
      <form id="task-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {formError && <FormMessage>{formError}</FormMessage>}
        {options.isError && (
          <FormMessage>
            Nie udało się wczytać klientów i osób.{' '}
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => void options.refetch()}
            >
              Spróbuj ponownie
            </button>
          </FormMessage>
        )}
        <Field label="Tytuł" error={errors.title}>
          <Input
            autoFocus
            value={values.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Co trzeba zrobić?"
            maxLength={300}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Klient"
            error={errors.clientId}
            hint={clientLocked ? 'Zgłoszenie z portalu zostaje przy kliencie, który je złożył.' : undefined}
          >
            <Combobox
              value={values.clientId ?? NONE}
              onValueChange={setClient}
              options={clientOptions}
              placeholder={loadingOptions ? 'Wczytywanie…' : 'Bez klienta'}
              searchPlaceholder="Szukaj klienta…"
              disabled={loadingOptions || clientLocked}
            />
          </Field>
          <Field label="Projekt" error={errors.projectId}>
            <Combobox
              value={values.projectId ?? NONE}
              onValueChange={setProject}
              options={projectOptions}
              placeholder={loadingOptions ? 'Wczytywanie…' : 'Bez projektu'}
              searchPlaceholder="Szukaj projektu…"
              disabled={loadingOptions}
            />
          </Field>
          <Field
            label="Usługa"
            error={errors.serviceId}
            hint={!values.clientId ? 'Najpierw wybierz klienta' : undefined}
          >
            <Select
              value={values.serviceId ?? NONE}
              onValueChange={(v) => set('serviceId', v === NONE ? null : v)}
              options={serviceOptions}
              disabled={loadingOptions || !values.clientId}
            />
          </Field>
          <Field label="Wykonawca" error={errors.assigneeUserId}>
            <Combobox
              value={values.assigneeUserId ?? NONE}
              onValueChange={(v) => set('assigneeUserId', v === NONE ? null : v)}
              options={memberOptions}
              placeholder={loadingOptions ? 'Wczytywanie…' : 'Bez wykonawcy'}
              searchPlaceholder="Szukaj osoby…"
              disabled={loadingOptions}
            />
          </Field>
          <Field label="Termin" error={errors.dueDate}>
            <DateInput value={values.dueDate} onValueChange={(v) => set('dueDate', v)} />
          </Field>
          <Field label="Priorytet" error={errors.priority}>
            <Select
              value={values.priority}
              onValueChange={(v) => set('priority', v)}
              options={TASK_PRIORITIES.map((p) => ({ value: p, label: TASK_PRIORITY_LABELS[p] }))}
            />
          </Field>
        </div>
        <Field label="Opis" error={errors.descriptionMd} hint="Możesz użyć Markdown.">
          <Textarea
            value={values.descriptionMd}
            onChange={(e) => set('descriptionMd', e.target.value)}
            rows={4}
            placeholder="Szczegóły, linki, kryteria odbioru…"
          />
        </Field>
      </form>
    </Dialog>
  );
}

/** Kropka koloru klienta (kolor to dane klienta, nie token). */
export function ClientDot({ color }: { color: string | null | undefined }) {
  if (!color) return <Dot />;
  return (
    <span aria-hidden className="inline-block size-[7px] shrink-0 rounded-full" style={{ backgroundColor: color }} />
  );
}
